import { invitationCheckSchema, type SignInStep } from "@esi/shared";
import { ArrowLeft, Link2Off, MailCheck } from "lucide-react";
import { useEffect, useId, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Avatar } from "@/components/Avatar";
import { Notice } from "@/components/Notice";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { NewPasswordField, passwordLongEnough } from "../account/PasswordField.tsx";
import { api } from "../api.ts";
import { isResetHandoff, Link, useHistoryState } from "../router.tsx";
import { flowHeaders, readStep, startFlow, withFlow } from "./pre-session.ts";
import { authErrorMessage, SignInFlow, StandaloneState } from "./SignIn.tsx";

type Purpose = "invite" | "reset";

type State =
  | { kind: "checking" }
  | { kind: "invalid" }
  | { kind: "request" }
  | { kind: "sent" }
  | { kind: "set"; token: string; invitation: Invitation | null }
  | { kind: "continue"; email: string; step: SignInStep };

/**
 * Who invited whom, for a valid invitation (proposal ui-refresh-auth-console: a later API slice
 * returns it for a valid, unexpired token only). An older API answers `valid` alone.
 */
interface Invitation {
  tenantName: string;
  email: string;
  invitedBy: string | null;
}

/** The invitation's description, or null (the generic invitation) when it is absent or malformed. */
function readInvitation(body: unknown): Invitation | null {
  const parsed = invitationCheckSchema.safeParse(body);
  if (!parsed.success) {
    return null;
  }
  const { tenantName, email, invitedBy } = parsed.data;
  return tenantName && email ? { tenantName, email, invitedBy: invitedBy ?? null } : null;
}

/** Takes the token out of the URL fragment (`#token=`), and the fragment out of the URL. */
function takeToken(): string | null {
  const token = new URLSearchParams(window.location.hash.slice(1)).get("token");
  if (window.location.hash) {
    window.history.replaceState(window.history.state, "", window.location.pathname);
  }
  return token;
}

/**
 * The pages of the links ESI emails: an invitation (first password, 72 h) and a password
 * reset (30 min). The token is read from the fragment, so it never reaches a server log; an
 * unknown, used or expired link offers a new one without saying whether the account exists.
 */
export function AccountLinkPage({ purpose }: { purpose: Purpose }) {
  const { t } = useTranslation();
  const id = useId();
  const [state, setState] = useState<State>({ kind: "checking" });
  const [password, setPassword] = useState("");
  // Forgot password at sign-in hands over the email typed there (history state; pure read).
  const historyState = useHistoryState();
  const handoff = purpose === "reset" && isResetHandoff(historyState) ? historyState : null;
  const [email, setEmail] = useState(handoff?.resetEmail ?? "");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const copy = purpose === "invite" ? "invitation" : "reset";
  // Read once, before any effect: an effect replay (StrictMode) must not lose it.
  const [token] = useState(takeToken);
  const started = useRef(false);

  // biome-ignore lint/correctness/useExhaustiveDependencies: once per page, with the token read at mount
  useEffect(() => {
    if (started.current) {
      return;
    }
    started.current = true;
    void (async () => {
      await startFlow();
      if (!token) {
        setState({ kind: purpose === "reset" ? "request" : "invalid" });
        return;
      }
      try {
        const check =
          purpose === "invite" ? api.api.auth.invitation.check : api.api.auth.reset.check;
        const res = await check.$post({ json: { token } }, flowHeaders());
        const body = res.ok ? await res.json() : null;
        setState(
          body && "valid" in body && body.valid
            ? {
                kind: "set",
                token,
                invitation: purpose === "invite" ? readInvitation(body) : null,
              }
            : { kind: "invalid" },
        );
      } catch {
        setError(t("errors.network"));
        setState({ kind: "invalid" });
      }
    })();
  }, []);

  // Taken once: a reload or coming back to this entry starts empty. Forgot password from this
  // page's own sign-in step (after a link) lands on the same page: back to the request form.
  useEffect(() => {
    if (!handoff) {
      return;
    }
    setEmail(handoff.resetEmail);
    setState((current) => (current.kind === "continue" ? { kind: "request" } : current));
    window.history.replaceState(null, "", window.location.pathname + window.location.search);
  }, [handoff]);

  async function setNewPassword(token: string, invited: string) {
    if (!passwordLongEnough(password)) {
      setError(t("password.tooShort"));
      return;
    }
    setBusy(true);
    try {
      const route = purpose === "invite" ? api.api.auth.invitation : api.api.auth.reset;
      const result = await readStep(
        await withFlow(() => route.$post({ json: { token, password } }, flowHeaders())),
      );
      if (result.ok) {
        // The same browser continues to sign-in with the new password (MFA policy included).
        setState({ kind: "continue", email: invited, step: result.step });
        return;
      }
      if (result.error?.code === "invalid_link") {
        setState({ kind: "invalid" });
      }
      setError(authErrorMessage(result));
    } catch {
      setError(t("errors.network"));
    } finally {
      setBusy(false);
    }
  }

  async function requestLink() {
    setBusy(true);
    try {
      const route =
        purpose === "invite" ? api.api.auth.invitation.resend : api.api.auth.reset.request;
      const res = await withFlow(() =>
        route.$post({ json: { email: email.trim() } }, flowHeaders()),
      );
      if (!res.ok) {
        const refused = await readStep(res);
        setError(refused.ok ? t("errors.unexpected") : authErrorMessage(refused));
        return;
      }
      setError(null);
      setState({ kind: "sent" });
    } catch {
      setError(t("errors.network"));
    } finally {
      setBusy(false);
    }
  }

  const invitation = state.kind === "set" ? state.invitation : null;
  const heading =
    state.kind === "invalid"
      ? { title: t("link.expiredTitle"), lead: t("link.expiredLead"), icon: Link2Off }
      : state.kind === "sent"
        ? { title: t("link.sentTitle"), lead: t("link.sent"), icon: MailCheck }
        : state.kind === "request"
          ? { title: t(`link.${copy}.requestTitle`), lead: t(`link.${copy}.requestLead`) }
          : invitation
            ? {
                title: t("link.invitation.joinTitle", { tenant: invitation.tenantName }),
                lead: invitation.invitedBy
                  ? t("link.invitation.invitedBy", { name: invitation.invitedBy })
                  : t("link.invitation.lead"),
              }
            : { title: t(`link.${copy}.title`), lead: t(`link.${copy}.lead`) };

  return (
    <StandaloneState
      title={state.kind === "continue" ? undefined : heading.title}
      lead={state.kind === "checking" || state.kind === "continue" ? undefined : heading.lead}
      icon={"icon" in heading ? heading.icon : undefined}
    >
      {error ? <Notice tone="danger">{error}</Notice> : null}
      {state.kind === "checking" ? (
        <div className="flex flex-col gap-3" role="status">
          <span className="sr-only">{t("app.loading")}</span>
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-10 w-full" />
        </div>
      ) : null}

      {state.kind === "set" ? (
        <form
          className="flex flex-col gap-4"
          onSubmit={(e) => {
            e.preventDefault();
            void setNewPassword(state.token, invitation?.email ?? "");
          }}
        >
          {invitation ? (
            <div className="flex items-center gap-3 rounded-lg border border-line bg-surface-raised px-2.5 py-2">
              <Avatar name={invitation.email} />
              <span className="min-w-0 flex-1 truncate text-sm text-fg">{invitation.email}</span>
              {/* For password managers: the account this password belongs to. */}
              <input
                type="email"
                name="email"
                value={invitation.email}
                autoComplete="username"
                aria-label={t("signIn.email")}
                hidden
                readOnly
              />
            </div>
          ) : null}
          <NewPasswordField value={password} onChange={setPassword} autoFocus invalid={!!error} />
          <Button type="submit" size="lg" disabled={busy}>
            {t("password.set")}
          </Button>
        </form>
      ) : null}

      {state.kind === "invalid" || state.kind === "request" ? (
        <form
          className="flex flex-col gap-4"
          onSubmit={(e) => {
            e.preventDefault();
            void requestLink();
          }}
        >
          <div className="flex flex-col gap-1.5">
            <Label htmlFor={`${id}-email`}>{t("signIn.email")}</Label>
            <Input
              id={`${id}-email`}
              type="email"
              name="email"
              required
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="h-10"
            />
          </div>
          <Button type="submit" size="lg" disabled={busy}>
            {t("link.send")}
          </Button>
        </form>
      ) : null}

      {/* Mounted empty with the request form, so the sentence is announced once it arrives. */}
      {state.kind === "invalid" || state.kind === "request" || state.kind === "sent" ? (
        <p className="sr-only" role="status">
          {state.kind === "sent" ? t("link.sent") : ""}
        </p>
      ) : null}
      {state.kind === "sent" ? (
        <div className="flex flex-col gap-3">
          <Button
            type="button"
            variant="outline"
            size="lg"
            onClick={() => setState({ kind: "request" })}
          >
            {t("link.sendAgain")}
          </Button>
        </div>
      ) : null}

      {state.kind === "continue" ? (
        <SignInFlow initial={{ email: state.email, step: state.step }} />
      ) : null}

      {state.kind !== "continue" ? (
        <Link
          href="/"
          className="inline-flex items-center gap-1.5 self-start text-sm text-fg-secondary underline-offset-4 hover:text-fg hover:underline"
        >
          <ArrowLeft aria-hidden="true" className="size-4" strokeWidth={1.75} />
          {t("link.backToSignIn")}
        </Link>
      ) : null}
    </StandaloneState>
  );
}
